import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

// --- Types ---

export interface AutoApplyRun {
  id: number
  startedAt: string
  completedAt: string | null
  rulesEvaluated: number
  eventsCreated: number
  eventsApplied: number
  eventsFailed: number
  errors: Array<{ eventId: number; error: string }> | null
}

export interface SafetyStatus {
  autoApplyEnabled: boolean
  envVar: string
  lastRun: AutoApplyRun | null
}

/**
 * Ergebnis eines Rollbacks — Gegenstueck zu `RollbackResult` in
 * `backend/src/modules/automation/rollback.ts`.
 *
 * Der Weg zum Client ist NICHT fuer beide Routen derselbe:
 *  - Batch (`POST /automation/rollback/batch/`): 200-Body mit `results[]`,
 *    die Felder kommen VERBATIM an.
 *  - Einzel (`POST /automation/rollback/:eventId/`): antwortet bei `success: false`
 *    mit `nack(policyDenial ? 403 : 400, error)` — das Objekt wird gar nicht
 *    gesendet, nur der Meldungstext. Deshalb erkennt `PromotionList` den
 *    Reconcile-Fall dort am Praefix und nicht an `reconcileRequired`.
 */
export interface RollbackResult {
  success: boolean
  eventId: number
  error?: string
  /** Policy-Denial (READ_ONLY_MODE / Profil-Allowlist) — Einzelroute antwortet dafuer 403 statt 400. */
  policyDenial?: boolean
  /**
   * Scheibe Q: Amazon hat den Rollback BESTAETIGT, nur die lokale Statuszeile
   * scheiterte. `success` ist trotzdem `false` — der Fall verlangt Abgleich,
   * NICHT Wiederholung: ein zweiter Rollback schickte eine bereits ausgefuehrte
   * Ruecknahme erneut an Amazon.
   */
  reconcileRequired?: boolean
}

/**
 * Praefix, mit dem `settleFailureResult` (backend/src/modules/automation/rollback.ts)
 * jede Meldung dieses Falls einleitet. Backendseitig an GENAU EINER Stelle gebildet.
 *
 * Es ist eine STRING-Kopplung ueber die Systemgrenze und als solche fragil — sie ist
 * hier bewusst als benannte Konstante gebunden statt inline verstreut, und
 * `backend/tests/rollout/fehleranzeige-praefix.test.ts` zementiert beide Seiten
 * gegeneinander (Drift-Anker). Wer das Literal aendert, bekommt dort einen roten Test
 * statt einer still verschwindenden Anzeige.
 */
export const RECONCILE_REQUIRED_PREFIX = 'RECONCILE_REQUIRED:'

// --- Hooks ---

export function useAutoApplyRuns(query: { limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams()
  if (query.limit) params.set('limit', String(query.limit))
  if (query.offset) params.set('offset', String(query.offset))
  const qs = params.toString()

  return useQuery({
    queryKey: ['autoApplyRuns', query],
    queryFn: () => api.get<{ runs: AutoApplyRun[]; limit: number; offset: number }>(
      `/automation/auto-apply/runs/${qs ? `?${qs}` : ''}`,
    ),
    refetchInterval: 60_000,
  })
}

export function useAutoApplyRunDetail(id: number) {
  return useQuery({
    queryKey: ['autoApplyRun', id],
    queryFn: () => api.get<{ run: AutoApplyRun }>(`/automation/auto-apply/runs/${id}/`),
    enabled: id > 0,
  })
}

export function useSafetyStatus() {
  return useQuery({
    queryKey: ['safetyStatus'],
    queryFn: () => api.get<SafetyStatus>('/automation/safety/status/'),
    refetchInterval: 30_000,
  })
}

export function useTriggerAutoApply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ run: { runId: number; eventsApplied: number; eventsFailed: number } }>(
      '/automation/auto-apply/trigger/',
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['autoApplyRuns'] })
      qc.invalidateQueries({ queryKey: ['safetyStatus'] })
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

export function useRollbackEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: number; reason: string }) =>
      api.post<{ rollback: RollbackResult }>(`/automation/rollback/${eventId}/`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['promotionEvents'] })
      qc.invalidateQueries({ queryKey: ['promotionStats'] })
    },
  })
}

// `useBatchRollback` ist mit Scheibe S entfernt worden (Registerpunkt
// DEAD-ROLLBACK-EXPORTS, Teilschluss): der Hook hatte im gesamten Frontend NULL
// Konsumenten. Die Route `POST /automation/rollback/batch/` bleibt bestehen und
// backendseitig getestet — nur die ungenutzte Client-Bindung ist weg. Eine
// Bulk-Rollback-Oberflaeche waere neue Funktionalitaet mit eigenem Risikoprofil
// (bis 100 bei Amazon irreversible Ruecknahmen pro Aufruf) und braucht einen
// eigenen Zuschnitt, keinen stillen Wiederanschluss dieses Hooks.
